/**
 * <s-product-recommendations> — native Shopify product recommendations.
 *
 * Fetches `${data-url}?product_id=…&limit=…&intent=related` (data-url is
 * routes.product_recommendations_url + ".json", written by Liquid) and renders each product
 * with the <template data-card> markup into [data-list]. The server-rendered cards already in
 * [data-list] (same-collection products) stay as the fallback when the request fails or
 * returns nothing; the component unhides itself when it has at least one card.
 *
 * Money uses data-money-format (shop.money_format) so prices match server-rendered cards.
 */
class SProductRecommendations extends HTMLElement {
  #ac;

  connectedCallback() {
    this.#ac = new AbortController();
    const list = this.querySelector('[data-list]');
    if (list?.children.length) this.hidden = false;
    this.#load();
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  async #load() {
    const { url, productId, limit = '8' } = this.dataset;
    if (!url || !productId) return;
    try {
      const params = new URLSearchParams({ product_id: productId, limit, intent: 'related' });
      const response = await fetch(`${url}?${params}`, { signal: this.#ac.signal });
      if (!response.ok) return;
      const data = await response.json();
      const products = Array.isArray(data?.products) ? data.products : [];
      if (products.length) this.#render(products);
    } catch {
      // Keep the server-rendered fallback.
    }
  }

  #render(products) {
    const list = this.querySelector('[data-list]');
    const template = this.querySelector('template[data-card]');
    if (!list || !template) return;

    const fragment = document.createDocumentFragment();
    for (const product of products) {
      if (typeof product?.url !== 'string' || !product.url.startsWith('/')) continue;
      const node = template.content.cloneNode(true);
      const link = node.querySelector('[data-link]');
      const image = node.querySelector('[data-image]');
      const price = node.querySelector('[data-price]');
      const compare = node.querySelector('[data-compare]');
      const badge = node.querySelector('[data-badge]');
      const soldOut = node.querySelector('[data-sold-out]');

      if (link) {
        link.href = product.url;
        link.textContent = product.title || '';
      }
      const src = product.featured_image || product.images?.[0];
      if (image instanceof HTMLImageElement && typeof src === 'string') {
        const base = src.startsWith('//') ? `https:${src}` : src;
        const sized = new URL(base, location.origin);
        sized.searchParams.set('width', '600');
        image.src = sized.toString();
        image.alt = product.title || '';
      } else {
        image?.remove();
      }

      const onSale = Number(product.compare_at_price) > Number(product.price);
      if (price) price.textContent = this.#money(product.price);
      if (compare) {
        if (onSale) compare.textContent = this.#money(product.compare_at_price);
        else compare.closest('[data-compare-wrap]')?.remove();
      }
      if (badge) {
        if (onSale) {
          const percent = Math.round((1 - product.price / product.compare_at_price) * 100);
          badge.textContent = (badge.dataset.template || '').replace('__P__', String(percent));
        } else {
          badge.closest('[data-badge-wrap]')?.remove();
        }
      }
      if (soldOut && product.available !== false) soldOut.remove();
      fragment.append(node);
    }

    if (!fragment.childNodes.length) return;
    list.replaceChildren(fragment);
    this.hidden = false;
  }

  #money(cents) {
    const value = Number(cents) / 100;
    const format = this.dataset.moneyFormat || '{{amount}}';
    const pattern = /\{\{\s*(\w+)\s*\}\}/;
    const kind = format.match(pattern)?.[1] || 'amount';
    const fixed = (decimals, thousands, decimal) => {
      const [whole, fraction] = value.toFixed(decimals).split('.');
      const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return fraction ? `${grouped}${decimal}${fraction}` : grouped;
    };
    let amount;
    switch (kind) {
      case 'amount_no_decimals': amount = fixed(0, ',', '.'); break;
      case 'amount_with_comma_separator': amount = fixed(2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': amount = fixed(0, '.', ','); break;
      case 'amount_with_space_separator': amount = fixed(2, ' ', ','); break;
      case 'amount_with_apostrophe_separator': amount = fixed(2, "'", '.'); break;
      default: amount = fixed(2, ',', '.');
    }
    const text = document.createElement('textarea');
    text.innerHTML = format.replace(pattern, amount);
    return text.value;
  }
}

if (!customElements.get('s-product-recommendations')) {
  customElements.define('s-product-recommendations', SProductRecommendations);
}
